import React, { type FC } from "react";

import cn from "classnames";

import { CHECKBOX_TEST_ID, CHECKBOX_DISPLAY_NAME } from "./vars";
import { type TGlyphSizes } from "../../assets/types/icons";
import { useMouseDownAnimation } from "../../hooks/useMouseDownAnimation";
import { extractProps } from "../../utils/react";
import { Icon } from "../Icon";

import type { CheckboxIconName, CheckboxProps } from "./types";
import type { GlyphSizeNames } from "../Icon/types";

import classes from "./Checkbox.styl";

/**
 * Базовый компонент, с помощью которого пользователь может выбрать одну или несколько из списка доступных опций
 *
 * <a href="https://www.figma.com/design/vTg3KAvmAXA9LigeyiqCWL/%F0%9F%92%A0-Base-Components?m=auto&node-id=2-15593&t=xOS5YJTI37Iwp4AM-1" target="_blank">Спека</a>
 * @constructor
 */
export const Checkbox: FC<CheckboxProps> = ({
    size = "24",
    view = "primary",
    type = "check",
    state = "enabled",
    checked = false,
    onChange,
    name,
    "aria-label": ariaLabel,
    "data-tsid": tsid,
    ...props
}) => {
    const { customColorsStyles } = extractProps(props, ["customColorsStyles"]);

    const isDisabled = state === "disabled";

    const { clicked, mousedownHandler } = useMouseDownAnimation();

    const wrapperClasses = cn(
        classes["wrapper"],
        classes[`__view-${view}`],
        classes[`__size-${size}`],
        {
            [classes["__checked"]]: checked,
            [classes["__state-disabled"]]: isDisabled,
            [classes["__clicked"]]: clicked,
        },
        customColorsStyles,
    );

    const iconSize: Extract<GlyphSizeNames, "24+"> = "24+";

    const icon: CheckboxIconName = `glyph_${type}_${iconSize}`;

    const iconProps = {
        icon,
        size,
    } as TGlyphSizes;

    return (
        <div
            className={wrapperClasses}
            onMouseDown={mousedownHandler}
            data-tsid={tsid}
            data-uikit={CHECKBOX_DISPLAY_NAME}
        >
            <div className={classes["checked-overlay"]}>
                <Icon {...iconProps} />
            </div>
            <input
                type="checkbox"
                name={name}
                className={classes["checkbox"]}
                disabled={isDisabled}
                checked={onChange ? checked : undefined}
                defaultChecked={!onChange ? checked : undefined}
                data-tsid={CHECKBOX_TEST_ID}
                onChange={onChange}
                aria-label={ariaLabel}
            />
            <div className={classes["checkbox-outline"]}></div>
        </div>
    );
};

Checkbox.displayName = CHECKBOX_DISPLAY_NAME;

export default Checkbox;
